/* ============================================================================
   AKINOD — Cinématique 3D au défilement
   ----------------------------------------------------------------------------
   Moteur de la section `akinod-cinematique`. Il ne s'exécute que si la sonde
   posée dans la section a validé WebGL et l'absence de demande de mouvement
   réduit (`data-ak-cine-mode="3d"`). Dans le cas contraire le HTML est déjà
   dans son état de repli : ce fichier n'a rien à faire.

   Trois règles de coût, dans l'ordre d'importance :

     1. Rien n'est téléchargé au chargement de la page. Three.js, les loaders,
        l'HDR et le premier GLB ne partent que lorsque la zone approche du
        viewport (IntersectionObserver, marge d'une fois et demie l'écran).
     2. `setAnimationLoop` n'est armé que tant que la zone est à l'écran. Hors
        champ : aucune frame, aucun travail GPU.
     3. Les modèles secondaires sont chargés à la demande. Sur une page d'accueil
        la plupart des visiteurs ne cliqueront jamais sur le second modèle ; on
        ne leur fait pas payer ses 170 Ko. Il est tout de même préchargé dès que
        l'avancement dépasse la moitié de la course, pour que le clic soit
        instantané chez ceux qui vont jusqu'au bout.

   Toutes les ressources sont celles du configurateur (`akinod-product-v2`) :
   même importmap `three`, mêmes loaders, mêmes GLB. Aucun octet dupliqué.

   Le repérage des matières se fait sur `userData` et non sur le nom des noeuds.
   Les GLB portent dans `extras` un champ `material` (metal / handle /
   textEngraving) et un champ `materialConfig` (metal1 pour l'acier poli,
   metalskew et metal2 pour les entretoises). Les noms de noeuds, eux, changent
   d'un export Blender à l'autre.
   ========================================================================== */

(() => {
  'use strict';

  /* Distance de garage des modèles inactifs, en unités de scène. Assez loin
     pour sortir du champ de la caméra avant la fin de l'interpolation. */
  const PARK = 11;

  /* Dislocation finale : décalage et rotation de chaque couvert une fois le
     rebond amorti. Repris tel quel de la maquette de référence — ces valeurs
     composent le « bouquet », elles ne se rejouent pas au hasard. */
  const BURST = {
    'blade-w': { p: [0.02, 0.05, 0.30], r: [0, 0.14, 0] },
    'fork-w': { p: [-0.05, -0.10, 0.19], r: [0, -0.10, 0] },
    'spoon-w': { p: [0.04, 0.09, -0.26], r: [0.72, -0.16, 0] },
  };

  /* Pose déployée du 13:25 : les outils ouverts en éventail, les deux coques
     écartées. Angles en radians. */
  const DEPLOY = { spoon: 3.05, breadknife: 2.35, fork: -2.0, bottleopener: -2.3 };

  const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
  const smoothstep = (t) => { t = clamp01(t); return t * t * (3 - 2 * t); };

  /* Chute libre jusqu'au contact, puis un rebond unique qui décélère vers la
     pose flottante. Un seul contact : deux rebonds font « balle », pas
     « couvert ».

     Le contact tombait à 45 % de la course sur tous les écrans. Sur téléphone
     c'était long : il fallait faire défiler plus d'un écran avant de voir les
     couverts se poser (retour HOM-002). Le point d'impact est donc réglable,
     et plus tôt en mobile ; il est fixé au démarrage (voir `IMPACT`). */
  let IMPACT = 0.45;

  function heightAt(p) {
    if (p < IMPACT) {
      const q = p / IMPACT;
      return 1.9 * (1 - q * q);
    }
    const q = clamp01((p - IMPACT) / (1 - IMPACT));
    return 0.66 * (1 - Math.pow(1 - q, 3));
  }

  /* Secousse de caméra, gaussienne très étroite centrée sur l'impact. */
  const shakeAt = (p) => Math.exp(-Math.pow((p - IMPACT) / 0.018, 2)) * 0.045;

  /* Lit une couleur de la charte plutôt que de la recopier : l'ombre portée
     doit rester teintée encre même si le jeton change. */
  function readInk(host) {
    const probe = document.createElement('span');
    probe.style.cssText = 'position:absolute;visibility:hidden;color:var(--ak-ink)';
    host.appendChild(probe);
    const raw = getComputedStyle(probe).color;
    probe.remove();
    const m = /(\d+)\D+(\d+)\D+(\d+)/.exec(raw);
    return m ? [+m[1], +m[2], +m[3]] : [21, 20, 15]; // dernier recours : l'encre de la charte
  }

  /* ------------------------------------------------------------------------ */
  /*  Amorçage                                                                 */
  /* ------------------------------------------------------------------------ */

  const teardowns = new WeakMap();

  function boot(root) {
    if (root.dataset.akCineBound) return;
    root.dataset.akCineBound = '1';

    // La sonde en ligne a déjà tranché. « flat » = le repli est en place.
    if (root.dataset.akCineMode !== '3d') return;

    const zone = root.querySelector('[data-ak-cine-zone]');
    const stage = root.querySelector('[data-ak-cine-stage]');
    const cfgEl = root.querySelector('[data-ak-cine-config]');
    if (!zone || !stage || !cfgEl) return fail(root);

    let cfg;
    try {
      cfg = JSON.parse(cfgEl.textContent);
    } catch (e) {
      return fail(root, e);
    }
    if (!cfg.models || !cfg.models.length) return fail(root);

    // Le point d'impact de l'écran courant (HOM-002).
    const petitEcran = window.matchMedia('(max-width: 640px)').matches;
    const impactReglage = petitEcran ? cfg.impactSm : cfg.impact;
    if (typeof impactReglage === 'number' && impactReglage > 0.1 && impactReglage < 0.9) IMPACT = impactReglage;

    root.classList.add('is-loading');

    // Déclencheur de chargement : une fois et demie la hauteur d'écran d'avance.
    // De quoi absorber un défilement rapide sans précharger pour rien.
    const arm = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      arm.disconnect();
      start(root, zone, stage, cfg).catch((e) => fail(root, e));
    }, { rootMargin: '150% 0px' });

    arm.observe(zone);
    teardowns.set(root, () => arm.disconnect());
  }


  /* Ramène une adresse d'asset sur l'origine de la page.
     ------------------------------------------------------------------------
     `asset_url` renvoie une adresse absolue vers le domaine Shopify
     (`//boutique.myshopify.com/cdn/shop/t/…`). Pour une image ou un script
     classique, peu importe. Pour un `import()` de module, c'est décisif :
     Safari applique la politique CORS aux modules et **refuse** un import
     d'une autre origine sans en-tête d'autorisation — « Cross-origin script
     load denied by Cross-Origin Resource Sharing policy ». Le moteur tombait
     alors en repli, sans que rien ne s'affiche à l'écran.

     Deux cas où les origines diffèrent :
       · en développement local, la page vient de `localhost:9393` ;
       · en production sur un domaine propre (`www.akinod.fr`), les assets
         restent annoncés sur `*.myshopify.com`.

     Shopify sert les chemins `/cdn/…` depuis le domaine de la vitrine : on
     garde donc le chemin et on laisse tomber le domaine. C'est exactement ce
     que fait déjà, dans `snippets/scripts.liquid`, l'importmap de `three`, qui,
     elle, n'a jamais échoué.                                                    */
  function memeOrigine(url) {
    try {
      const u = new URL(url, location.href);
      return u.origin === location.origin ? url : u.pathname + u.search;
    } catch (e) {
      return url;
    }
  }

  /* Retour à la bannière de repli. Jamais de section masquée : mieux vaut une
     image fixe qu'un trou dans la page. */
  function fail(root, err) {
    if (err) console.warn('[akinod-cine]', err);
    root.classList.remove('is-3d', 'is-loading', 'is-ready');
    root.dataset.akCineMode = 'flat';
    const down = teardowns.get(root);
    if (down) { down(); teardowns.delete(root); }
  }

  /* ------------------------------------------------------------------------ */
  /*  Moteur                                                                   */
  /* ------------------------------------------------------------------------ */

  async function start(root, zone, stage, cfg) {
    // `three` passe par l'unique importmap du thème, déclarée dans
    // `snippets/scripts.liquid` : le module est partagé avec le configurateur,
    // les loaders résolvent vers la même instance et il n'existe qu'un seul
    // exemplaire de la bibliothèque.
    const [THREE, gltfMod, rgbeMod, dracoMod] = await Promise.all([
      import('three'),
      import(memeOrigine(cfg.gltf)),
      import(memeOrigine(cfg.rgbe)),
      import(memeOrigine(cfg.draco)),
    ]);
    const { GLTFLoader } = gltfMod;
    const { RGBELoader } = rgbeMod;
    const { DRACOLoader } = dracoMod;

    /* --- Rendu ----------------------------------------------------------- */

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    stage.appendChild(renderer.domElement);

    // Filet de sécurité posé tout de suite : si la suite échoue (GLB illisible,
    // réseau coupé), `fail()` déclenchera au moins la libération du contexte
    // WebGL. Il est remplacé par le démontage complet en fin d'initialisation.
    teardowns.set(root, () => {
      renderer.setAnimationLoop(null);
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.remove();
    });

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(30, 1, 0.05, 100);
    camera.position.set(0, 0.35, 7.2);
    camera.lookAt(0, 0, 0);
    const camBase = camera.position.clone();

    const key = new THREE.DirectionalLight(0xffffff, 1.35);
    key.position.set(4, 8, 6);
    scene.add(key);
    scene.add(new THREE.HemisphereLight(0xfff6e6, 0x6b6250, 0.5));

    /* --- Matières -------------------------------------------------------- */

    // Constantes physiques (acier brossé, résine mate), pas des couleurs de
    // marque : elles n'ont pas leur place dans les jetons de la charte.
    const disposables = [];
    const brushed = brushedNormal(THREE);
    const grain = grainNormal(THREE);
    disposables.push(brushed, grain);

    const matSteel = new THREE.MeshStandardMaterial({
      color: 0xdde0e6, metalness: 1, roughness: 0.1, envMapIntensity: 1.55,
      normalMap: brushed, normalScale: new THREE.Vector2(0.035, 0.035),
    });
    const matMatte = new THREE.MeshStandardMaterial({
      color: 0xe7eaef, metalness: 0.8, roughness: 0.58, envMapIntensity: 0.72,
      normalMap: grain, normalScale: new THREE.Vector2(0.3, 0.3),
    });
    const matPattern = new THREE.MeshStandardMaterial({
      color: 0xffffff, metalness: 0.12, roughness: 0.55, envMapIntensity: 0.75,
    });
    // La même résine, pour les faces du 13:25 : elles ont leur propre texture (voir « Plaques »).
    const matPlaque = new THREE.MeshStandardMaterial({
      color: 0xffffff, metalness: 0.12, roughness: 0.55, envMapIntensity: 0.75,
    });
    disposables.push(matSteel, matMatte, matPattern, matPlaque);

    /* --- Ombres portées --------------------------------------------------- */

    // Pas de shadow map : trois taches floues suffisent à poser les couverts,
    // pour une fraction du coût GPU (et le sol est un dégradé, pas un plan).
    const ink = readInk(root);
    const blobTex = blobTexture(THREE, ink);
    disposables.push(blobTex);

    function makeBlob(sx, sz) {
      const geo = new THREE.PlaneGeometry(1, 1);
      const mat = new THREE.MeshBasicMaterial({ map: blobTex, transparent: true, depthWrite: false });
      const m = new THREE.Mesh(geo, mat);
      m.rotation.x = -Math.PI / 2;
      m.scale.set(sx, sz, 1);
      m.position.y = -1.34;
      scene.add(m);
      disposables.push(geo, mat);
      return m;
    }

    /* --- Environnement ---------------------------------------------------- */

    await new Promise((resolve) => {
      new RGBELoader().load(cfg.hdr, (hdr) => {
        const pmrem = new THREE.PMREMGenerator(renderer);
        scene.environment = pmrem.fromEquirectangular(hdr).texture;
        // La source équirectangulaire et le générateur ne servent plus une fois
        // la cubemap filtrée produite : les garder, c'est retenir plusieurs Mo
        // de VRAM pour rien.
        hdr.dispose();
        pmrem.dispose();
        resolve();
      }, undefined, () => resolve()); // sans HDR la scène reste lisible, juste plus plate
    });

    /* --- Chargement des modèles -------------------------------------------- */

    const draco = new DRACOLoader();
    draco.setDecoderPath(String(cfg.dracoWrapper).split('?')[0].replace(/[^/]+$/, ''));
    const loader = new GLTFLoader().setDRACOLoader(draco);

    const BASIS = new THREE.Matrix4().makeBasis(
      new THREE.Vector3(0, -1, 0),
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(-1, 0, 0),
    );

    function dressMesh(o, flottant) {
      const role = o.userData.material;
      const conf = o.userData.materialConfig;
      const name = o.name || '';
      // Repères d'interface du configurateur : ils n'ont rien à faire ici.
      if (role === 'textEngraving' || name.startsWith('hud') || name.startsWith('hotspot')) {
        o.visible = false;
        return;
      }
      if (role === 'handle' && PLAQUES.test(name)) projeterPlaque(o, flottant);
      else if (role === 'handle') o.material = matPattern;
      else if (conf === 'metalskew' || conf === 'metal2') o.material = matMatte;
      else o.material = matSteel;
    }

    // Recentre et met à l'échelle un modèle dans un groupe porteur.
    function frame(obj, size) {
      obj.quaternion.setFromRotationMatrix(BASIS);
      const holder = new THREE.Group();
      holder.add(obj);
      obj.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(obj);
      const dim = box.getSize(new THREE.Vector3());
      obj.position.sub(box.getCenter(new THREE.Vector3()));
      holder.scale.setScalar(size / Math.max(dim.x, dim.y, dim.z));
      return holder;
    }

    const BASE_SIZE = 2.05;
    const entries = cfg.models.map((m, i) => ({
      def: m,
      index: i,
      loaded: false,
      loading: null,
      holder: null,
      baseScale: 1,
      parts: {},
      x: i === 0 ? 0 : PARK,
      targetX: i === 0 ? 0 : PARK,
    }));

    /* --- Motifs -------------------------------------------------------------- */

    // Déclaré avant le premier chargement : chaque modèle qui arrive doit
    // pouvoir reprendre le motif courant, y compris le tout premier.
    const texCache = new Map();
    const current = { model: 0, pattern: 0 };

    function applyPattern(i) {
      const def = (cfg.patterns || [])[i];
      appliquerPlaque(i, def);
      if (!def || !def.url) { matPattern.map = null; matPattern.needsUpdate = true; return; }
      let tex = texCache.get(def.url);
      if (!tex) {
        /* Les adresses de Shopify sont relatives au protocole (`//…`). Servie en
           http (un aperçu local), la page les demandait en http et le CDN
           renvoyait vers https : une redirection qu'une texture WebGL ne
           traverse pas toujours. On demande https d'emblée. */
        tex = chargerTexture(def.url, def.repeatV, () => {
          /* La bande d'un bois ou d'un aluminium manque dans les ressources du
             thème : on retombe sur la photo carrée du bloc, répétée. */
          if (!def.repli) return;
          const repli = chargerTexture(def.repli, 6.28);
          texCache.set(def.url, repli);
          if (current.pattern === i) { matPattern.map = repli; matPattern.needsUpdate = true; }
        });
        texCache.set(def.url, tex);
      }
      matPattern.map = tex;
      matPattern.needsUpdate = true;
    }

    function chargerTexture(url, repeatV, siEchec) {
      const tex = new THREE.TextureLoader().load(https(url), undefined, undefined, siEchec);
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.flipY = false; // convention glTF : l'origine UV est en haut
      tex.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
      /* Une photo carrée posée sur un manche déplié pour une bande six fois
         plus haute que large : on la répète dans la hauteur plutôt que de
         l'étirer (HOM-001). Une bande, elle, se pose telle quelle. */
      if (typeof repeatV === 'number' && repeatV > 1) {
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(1, repeatV);
      }
      disposables.push(tex);
      return tex;
    }

    /* --- Plaques du 13:25 ------------------------------------------------------ */

    /* Les coordonnées de texture du 13:25 partagent la largeur de l'image entre
       ses deux coques : `handle-top` en prend la moitié gauche, `handle-bottom`
       la moitié droite. Chaque coque recevait donc une demi-bande du 12:34,
       élargie 2,4 fois (« les 13:25 sont étirés sur la home », Isa, 30/09).

       On fait comme la fiche produit, dont le 13:25 est jugé impeccable (30/09) :
       la face imprimée de chaque coque est projetée à plat et reçoit la bande
       entière ; les flancs sont en acier. La bande est celle du 13:25
       (`akinod-texture-1325-<id>.jpg`, aux proportions d'une face : 0,193), à
       défaut celle du 12:34, puis la photo du bloc, toujours cadrée sans
       déformation.

       Seul le sens du décor côté fourchette diffère. La fiche le tourne d'un
       demi-tour, parce que ses deux corps restent dos à dos. La pose « float »
       de l'accueil retourne le corps fourchette (`bottom-w`, demi-tour autour
       de la longueur) pour montrer ses deux faces côte à côte : ce retournement
       fait déjà tourner le décor d'un demi-tour, on le pose donc à l'endroit
       pour que les deux coques se lisent dans le même sens, sans miroir. */
    const PLAQUES = /^handle-(top|bottom)$/;
    let plaqueAspect = 0; // largeur / hauteur d'une face, relevée à la projection
    const plaques = new Map();

    function projeterPlaque(o, retournee) {
      const bas = o.name === 'handle-bottom';
      const signe = bas ? -1 : 1; // la face imprimée regarde vers +y (couteau) ou -y (fourchette)
      const src = o.geometry.index ? o.geometry.toNonIndexed() : o.geometry;
      const pos = src.attributes.position.array;
      const nor = src.attributes.normal && src.attributes.normal.array;
      const uv0 = src.attributes.uv && src.attributes.uv.array;
      if (!nor) { o.material = matPlaque; return; }

      const n = src.attributes.position.count;
      const face = [], flancs = [];
      for (let t = 0; t < n / 3; t++) {
        const ny = (nor[9 * t + 1] + nor[9 * t + 4] + nor[9 * t + 7]) / 3;
        (ny * signe > 0.4 ? face : flancs).push(t);
      }
      const P = new Float32Array(n * 3), N = new Float32Array(n * 3), UV = new Float32Array(n * 2);
      let w = 0;
      face.concat(flancs).forEach((t) => {
        for (let v = 0; v < 3; v++, w++) {
          const s = 3 * t + v;
          for (let k = 0; k < 3; k++) { P[w * 3 + k] = pos[s * 3 + k]; N[w * 3 + k] = nor[s * 3 + k]; }
          // Les flancs gardent les coordonnées du modèle, pour le brossé de l'acier.
          if (uv0) { UV[w * 2] = uv0[s * 2]; UV[w * 2 + 1] = uv0[s * 2 + 1]; }
        }
      });

      // Projection plane de la face : u dans la largeur (z), v dans la longueur (x).
      const nf = face.length * 3;
      let minx = Infinity, maxx = -Infinity, minz = Infinity, maxz = -Infinity;
      for (let i = 0; i < nf; i++) {
        const x = P[i * 3], z = P[i * 3 + 2];
        if (x < minx) minx = x; if (x > maxx) maxx = x;
        if (z < minz) minz = z; if (z > maxz) maxz = z;
      }
      const sx = (maxx - minx) || 1, sz = (maxz - minz) || 1;
      for (let i = 0; i < nf; i++) {
        const x = P[i * 3], z = P[i * 3 + 2];
        UV[i * 2] = bas && retournee ? (z - minz) / sz : (maxz - z) / sz;
        UV[i * 2 + 1] = bas && !retournee ? (maxx - x) / sx : (x - minx) / sx;
      }
      if (nf) plaqueAspect = sz / sx;

      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(P, 3));
      geo.setAttribute('normal', new THREE.BufferAttribute(N, 3));
      geo.setAttribute('uv', new THREE.BufferAttribute(UV, 2));
      if (nf) geo.addGroup(0, nf, 1);
      if (n > nf) geo.addGroup(nf, n - nf, 0);
      if (src !== o.geometry) src.dispose();
      o.geometry.dispose();
      o.geometry = geo;
      o.material = [matSteel, matPlaque];
    }

    function appliquerPlaque(i, def) {
      if (!plaqueAspect) return; // pas de 13:25 chargé : rien à préparer
      const poser = (tex) => {
        if (current.pattern !== i) return;
        matPlaque.map = tex;
        matPlaque.needsUpdate = true;
      };
      const sources = def ? [def.bande1325, def.url, def.repli].filter(Boolean) : [];
      if (!sources.length) return poser(null);
      const deja = plaques.get(i);
      if (deja) { if (deja.tex || deja.echec) poser(deja.tex); return; }

      // La texture précédente reste en place tant que la nouvelle n'est pas prête.
      const entree = { tex: null };
      plaques.set(i, entree);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      let k = 0;
      img.onerror = () => {
        if (++k < sources.length) { img.src = https(sources[k]); return; }
        entree.echec = true; // aucune image : résine unie plutôt que le décor précédent
        poser(null);
      };
      img.onload = () => {
        const FH = Math.min(2048, Math.max(512, img.naturalHeight));
        const FW = Math.max(64, Math.round(FH * plaqueAspect));
        const toile = document.createElement('canvas');
        toile.width = FW; toile.height = FH;
        // Cadrée sans déformation, comme sur la fiche produit : la bande couvre la face.
        const cover = Math.max(FW / img.naturalWidth, FH / img.naturalHeight);
        const ctx = toile.getContext('2d');
        ctx.translate(FW / 2, FH / 2);
        ctx.scale(cover, cover);
        ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
        const tex = new THREE.CanvasTexture(toile);
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.flipY = false;
        tex.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
        disposables.push(tex);
        entree.tex = tex;
        poser(tex);
      };
      img.src = https(sources[0]);
    }

    function https(url) {
      return url.startsWith('//') ? `https:${url}` : url;
    }

    /* --- Mise en page ---------------------------------------------------------- */

    function layout() {
      const w = stage.clientWidth;
      const h = stage.clientHeight;
      if (!w || !h) return;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      // Sur écran étroit, le modèle est réduit pour laisser respirer le
      // filigrane et la pile de commandes.
      const shrink = w <= 700 ? 0.78 : 1;
      entries.forEach((e) => {
        if (e.holder) e.holder.scale.setScalar(e.baseScale * shrink);
      });
    }

    function loadEntry(entry) {
      if (entry.loading) return entry.loading;
      entry.loading = new Promise((resolve, reject) => {
        loader.load(entry.def.url, (gltf) => {
          const obj = gltf.scene;

          if (entry.def.pose === 'float') {
            // Ouverture en éventail avant le cadrage : les décalages sont
            // exprimés dans le repère du modèle, pas dans celui de la scène.
            obj.traverse((o) => {
              if (DEPLOY[o.name] != null) o.rotation.y = DEPLOY[o.name];
              if (o.name === 'top-w') { o.position.z += 0.125; o.position.x -= 0.09; o.rotation.y = -0.07; }
              if (o.name === 'bottom-w') { o.position.z -= 0.125; o.position.x += 0.09; o.rotation.x = Math.PI; }
            });
          }

          obj.traverse((o) => { if (o.isMesh) dressMesh(o, entry.def.pose === 'float'); });

          if (entry.def.pose !== 'float') {
            obj.children.forEach((c) => {
              if (BURST[c.name]) entry.parts[c.name] = c;
            });
          }

          const size = BASE_SIZE * (entry.def.size || 1) * (cfg.scale || 1);
          entry.holder = frame(obj, size);
          entry.baseScale = entry.holder.scale.x;
          if (entry.def.pose === 'float') {
            entry.holder.rotation.z = -0.9;
            entry.holder.rotation.x = -0.04;
          }
          entry.holder.position.x = entry.x;
          scene.add(entry.holder);
          entry.blob = makeBlob(entry.def.pose === 'float' ? 2.6 : 3.0, entry.def.pose === 'float' ? 0.95 : 1.05);
          entry.loaded = true;
          applyPattern(current.pattern);
          layout(); // le modèle qui arrive doit adopter tout de suite l'échelle de l'écran
          resolve(entry);
        }, undefined, reject);
      });
      return entry.loading;
    }

    applyPattern(0);
    await loadEntry(entries[0]);

    /* --- Commandes ------------------------------------------------------------ */

    const marks = Array.from(root.querySelectorAll('[data-ak-cine-wm]'));
    const modelBtns = Array.from(root.querySelectorAll('[data-ak-cine-model]'));
    const patternBtns = Array.from(root.querySelectorAll('[data-ak-cine-pattern]'));

    /* --- Appel à l'action ------------------------------------------------------ *
     * Ce que l'utilisateur vient de composer à l'écran doit être ce qu'il retrouve
     * en arrivant sur la fiche : le bouton pointe donc vers la fiche de la matière
     * composée quand les tables en donnent une, et seulement à défaut vers celle du
     * modèle, motif courant en paramètre.
     *
     * Le gabarit a déjà posé un `href` correct pour l'état initial — cette
     * fonction ne fait que le suivre. Si aucune fiche n'est rattachée au modèle,
     * on ne touche à rien : le lien libre saisi dans l'éditeur reste en place. */
    const ctaLink = root.querySelector('[data-ak-cine-cta]');

    function syncCta() {
      if (!ctaLink) return;
      const produit = (cfg.models[current.model] || {}).produit;
      if (!produit) return;
      /* Chaque matière composée à l'écran a sa fiche : on y mène directement,
         plutôt que d'ouvrir le configurateur et de lui passer un paramètre.

         Deux tables, parce qu'il y a deux natures de pastille : un MOTIF imprimé
         (jungle, léopard…) et un MANCHE (bois massif, aluminium). Toutes deux
         portées par le configurable du modèle affiché, donc justes qu'on regarde
         le 12:34 ou le 13:25.

         Sans fiche, on retombe sur le configurateur — avec le motif en paramètre
         quand il y en a un, nu sinon. */
      const bloc = (cfg.patterns || [])[current.pattern] || {};
      const modele = cfg.models[current.model] || {};

      /* La langue vient de `Shopify.routes.root` (« /en/ », « / » en français), comme dans
         le configurateur. Le réglage `produit` est tapé dans l'éditeur, « /products/couverts-12-34 » :
         il ne la porte pas, et le lien menait un visiteur de /en à la page française. Les tables
         de fiches, elles, sont traduites : sur /de elles donnent l'identifiant allemand, qui
         n'existe qu'une fois préfixé. */
      const racine = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');
      const lien = produit.charAt(0) === '/' && racine && produit.indexOf(`${racine}/`) !== 0 ? racine + produit : produit;

      const entree = bloc.motif
        ? (modele.fiches || {})[bloc.motif]
        : (bloc.manche ? (modele.fichesManche || {})[bloc.manche] : null);
      const handle = entree && (entree.M || entree.B);

      if (handle) { ctaLink.setAttribute('href', `${racine}/products/${handle}`); return; }
      ctaLink.setAttribute(
        'href',
        bloc.motif
          ? lien + (lien.indexOf('?') === -1 ? '?' : '&') + 'motif=' + encodeURIComponent(bloc.motif)
          : lien,
      );
    }

    function selectModel(i) {
      const entry = entries[i];
      if (!entry || current.model === i) return;

      const commit = () => {
        current.model = i;
        syncCta();
        entries.forEach((e, j) => { e.targetX = j === i ? 0 : (j < i ? -PARK : PARK); });
        marks.forEach((el, j) => {
          el.classList.toggle('is-left', j < i);
          el.classList.toggle('is-right', j > i);
        });
        modelBtns.forEach((b, j) => {
          b.classList.toggle('is-active', j === i);
          b.setAttribute('aria-pressed', j === i ? 'true' : 'false');
        });
      };

      if (entry.loaded) return commit();

      // Chargement à la demande : le bouton reste enfoncé le temps du transfert
      // plutôt que de laisser croire à un clic perdu.
      const btn = modelBtns[i];
      if (btn) btn.disabled = true;
      loadEntry(entry).then(() => {
        if (btn) btn.disabled = false;
        commit();
      }).catch((e) => {
        if (btn) btn.disabled = false;
        console.warn('[akinod-cine]', e);
      });
    }

    modelBtns.forEach((b, i) => b.addEventListener('click', () => selectModel(i)));
    patternBtns.forEach((b, i) => b.addEventListener('click', () => {
      current.pattern = i;
      applyPattern(i);
      syncCta();
      patternBtns.forEach((x, j) => {
        x.classList.toggle('is-active', j === i);
        x.setAttribute('aria-pressed', j === i ? 'true' : 'false');
      });
    }));

    // L'éditeur de thème réinjecte la section : le lien rendu par le gabarit peut
    // avoir été recalculé pendant que la scène, elle, gardait son état.
    syncCta();

    // ResizeObserver sur le conteneur, pas d'écouteur `resize` global : la barre
    // d'URL mobile déclenche `resize` en continu alors que la scène, elle, ne
    // change pas forcément de taille.
    const ro = new ResizeObserver(layout);
    ro.observe(stage);
    layout();

    /* --- Boucle ------------------------------------------------------------------ */

    const cap = root.querySelector('[data-ak-cine-cap]');
    const models = root.querySelector('[data-ak-cine-models]');
    const swatches = root.querySelector('[data-ak-cine-swatches]');
    /* Les commandes apparaissent un temps fixe APRÈS l'impact : réglées à 55 %
       pour un impact à 45 %, elles suivent l'impact quand il avance, faute de
       quoi on les attendrait aussi longtemps qu'avant. */
    const revealAt = (typeof cfg.reveal === 'number' ? cfg.reveal : 0.55) + (IMPACT - 0.45);
    const clock = new THREE.Clock();

    let revealed = null;
    let prefetched = false;

    function progress() {
      const r = zone.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      return clamp01((window.innerHeight - r.top) / (window.innerHeight + total * 0.7));
    }

    function loop() {
      const t = clock.getElapsedTime();
      const p = progress();
      const e = smoothstep((p - IMPACT) / 0.5);
      const y = heightAt(p);

      entries.forEach((entry) => {
        if (!entry.loaded) return;
        entry.x += (entry.targetX - entry.x) * 0.075;
        const h = entry.holder;
        h.position.x = entry.x;

        if (entry.def.pose === 'float') {
          h.position.y = 0.18 + Math.sin(t * 0.8) * 0.05;
          h.rotation.y = Math.sin(t * 0.45) * 0.04;
          if (entry.blob) {
            entry.blob.position.x = entry.x;
            entry.blob.material.opacity = 0.8 * (1 - Math.min(1, Math.abs(entry.x) / 6));
          }
          return;
        }

        h.position.y = y - 0.38 + Math.sin(t * 0.9) * 0.025 * e;
        h.rotation.z = -0.14 * (1 - e);
        h.rotation.y = -0.45 * (1 - smoothstep(p)) + Math.sin(t * 0.5) * 0.03 * e;
        h.rotation.x = 0.05 * (1 - e) + 0.03 * e;

        Object.keys(entry.parts).forEach((k) => {
          const g = entry.parts[k];
          const o = BURST[k];
          g.position.set(o.p[0] * e, o.p[1] * e, o.p[2] * e);
          g.rotation.set(o.r[0] * e, o.r[1] * e, o.r[2] * e);
        });

        if (entry.blob) {
          entry.blob.position.x = entry.x;
          entry.blob.material.opacity =
            Math.max(0, 0.95 * (1 - Math.min(1, y / 1.9)) * (1 - Math.min(1, Math.abs(entry.x) / 6)));
        }
      });

      const s = shakeAt(p);
      camera.position.set(
        camBase.x + Math.sin(p * 880) * s,
        camBase.y + Math.cos(p * 760) * s * 0.6,
        camBase.z,
      );

      // Un seul basculement de classe par franchissement de seuil, pas soixante
      // écritures de classList par seconde.
      const show = p >= revealAt;
      if (show !== revealed) {
        revealed = show;
        [cap, models, swatches].forEach((el) => el && el.classList.toggle('is-revealed', show));
      }

      // À mi-course, l'intention est claire : on prépare le second modèle pour
      // que le clic soit immédiat.
      if (!prefetched && p >= 0.5 && entries[1] && !entries[1].loading) {
        prefetched = true;
        loadEntry(entries[1]).catch(() => {});
      }

      renderer.render(scene, camera);
    }

    /* --- Fenêtre d'activité ------------------------------------------------------- */

    // Hors champ, la boucle est désarmée : ni frame, ni travail GPU. C'est le
    // seul interrupteur — un onglet en arrière-plan suspend déjà le rAF sur
    // lequel `setAnimationLoop` est bâti.
    let running = false;
    const vis = new IntersectionObserver((es) => {
      const on = es.some((e) => e.isIntersecting);
      if (on === running) return;
      running = on;
      renderer.setAnimationLoop(on ? loop : null);
    }, { threshold: 0 });
    vis.observe(zone);

    root.classList.remove('is-loading');
    root.classList.add('is-ready');
    loop(); // première image immédiate, avant même que l'observateur ne réponde

    teardowns.set(root, () => {
      vis.disconnect();
      ro.disconnect();
      renderer.setAnimationLoop(null);
      scene.traverse((o) => { if (o.isMesh && o.geometry) o.geometry.dispose(); });
      disposables.forEach((d) => d.dispose && d.dispose());
      draco.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode) renderer.domElement.remove();
    });
  }

  /* ------------------------------------------------------------------------ */
  /*  Textures procédurales                                                    */
  /* ------------------------------------------------------------------------ */

  /* Rayures verticales très fines : c'est ce qui fait lire « inox brossé »
     plutôt que « plastique chromé » sous une lumière fixe. */
  function brushedNormal(THREE) {
    const c = document.createElement('canvas');
    c.width = 8; c.height = 256;
    const x = c.getContext('2d');
    for (let i = 0; i < 256; i++) {
      const v = 120 + ((Math.random() * 30) | 0);
      x.fillStyle = 'rgb(' + v + ',' + v + ',255)';
      x.fillRect(0, i, 8, 1);
    }
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(2, 2);
    return t;
  }

  /* Grain isotrope pour les entretoises mates. */
  function grainNormal(THREE) {
    const c = document.createElement('canvas');
    c.width = c.height = 128;
    const x = c.getContext('2d');
    const img = x.createImageData(128, 128);
    for (let i = 0; i < img.data.length; i += 4) {
      img.data[i] = 128 + ((Math.random() * 34) | 0) - 17;
      img.data[i + 1] = 128 + ((Math.random() * 34) | 0) - 17;
      img.data[i + 2] = 255;
      img.data[i + 3] = 255;
    }
    x.putImageData(img, 0, 0);
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(7, 7);
    return t;
  }

  /* Tache d'ombre douce, teintée avec l'encre de la charte. */
  function blobTexture(THREE, ink) {
    const c = document.createElement('canvas');
    c.width = c.height = 256;
    const x = c.getContext('2d');
    const rgb = ink.join(',');
    const g = x.createRadialGradient(128, 128, 8, 128, 128, 122);
    g.addColorStop(0, 'rgba(' + rgb + ',0.42)');
    g.addColorStop(0.55, 'rgba(' + rgb + ',0.16)');
    g.addColorStop(1, 'rgba(' + rgb + ',0)');
    x.fillStyle = g;
    x.fillRect(0, 0, 256, 256);
    return new THREE.CanvasTexture(c);
  }

  /* ------------------------------------------------------------------------ */
  /*  Cycle de vie                                                             */
  /* ------------------------------------------------------------------------ */

  const scan = (scope) => (scope || document).querySelectorAll('[data-ak-cine]').forEach(boot);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }

  // L'éditeur de thème réinjecte la section : ce module, lui, ne sera pas
  // réexécuté (même URL). On rebranche donc à la main, et on libère le contexte
  // WebGL de l'ancienne instance — leur nombre est très limité sur mobile.
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-ak-cine]').forEach((root) => {
      const down = teardowns.get(root);
      if (down) { down(); teardowns.delete(root); }
    });
  });
})();
